import type { EpochTimestamp } from '@getcircuit/schema'

import type { SuccessResponse, ClientContext, ErrorResponse } from '../../types'

type BaseParams = {
  teamId: string
  routeId: string
  signal?: AbortSignal
}

type DuplicatePlanParams = BaseParams & {
  /**
   * Title of the created route duplicate
   * @default Copy of the original route title
   */
  title?: string
  /**
   * New UTC time the route should be scheduled for
   * @default null
   */
  startsAt?: EpochTimestamp
}

type DuplicatePlanResponse =
  | SuccessResponse<{
      routeId: string
    }>
  | ErrorResponse

/**
 * Duplicates the `route` and its `stops` sub-collection and resets its state back to the default one.
 *
 * [Link to documentation](https://circuit-doc-schema.netlify.app/pages/api/route#duplicate-route)
 */
export function duplicateRoute(
  context: ClientContext,
  { teamId, routeId, title, startsAt, signal }: DuplicatePlanParams,
): Promise<DuplicatePlanResponse> {
  return context.request(`/teams/${teamId}/routes/${routeId}/duplicate`, {
    method: 'POST',
    withAuth: true,
    signal,
    json: { title, startsAt },
  })
}

/**
 * Hard-deletes a `route` from the team including all sub-collections.
 *
 * [Link to documentation](https://circuit-doc-schema.netlify.app/pages/api/route#delete-route)
 */
export function deleteRoute(
  context: ClientContext,
  { teamId, routeId, signal }: BaseParams,
): Promise<undefined | ErrorResponse> {
  return context.request(`/teams/${teamId}/routes/${routeId}`, {
    method: 'DELETE',
    signal,
    withAuth: true,
  })
}
